import { useEffect, useRef } from 'react'
import { RULES_SECTIONS, type RulesSectionId } from './content'
import './rules.css'

/**
 * The sticky section nav: one chip per section, scrolling horizontally, with the
 * active chip in the cyan chip token — the only cyan on this screen, so it can only mean "you are
 * here".
 *
 * No accordions anywhere on this page, which is what makes a nav worth having: everything is
 * expanded, so the chips move you rather than reveal anything.
 */
export default function RulesSectionNav({
  activeId,
  onSelect,
}: {
  activeId: RulesSectionId
  onSelect: (id: RulesSectionId) => void
}) {
  const railRef = useRef<HTMLDivElement>(null)

  // Keep the active chip in view as the reader scrolls the page — otherwise the nav silently
  // stops tracking once you are past the fourth section on a 390px screen.
  useEffect(() => {
    const rail = railRef.current
    if (!rail) return
    const chip = rail.querySelector<HTMLElement>(`[data-section="${activeId}"]`)
    chip?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [activeId])

  return (
    <nav className="rules-nav" aria-label="Rules sections">
      <div className="rules-nav__rail" ref={railRef}>
        {RULES_SECTIONS.map((section) => (
          <button
            key={section.id}
            type="button"
            data-section={section.id}
            className={`rules-nav__chip ${
              section.id === activeId ? 'rules-nav__chip--active' : ''
            }`}
            aria-current={section.id === activeId ? 'true' : undefined}
            onClick={() => onSelect(section.id)}
          >
            {section.title}
          </button>
        ))}
      </div>
    </nav>
  )
}
